import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowUpRight, ChevronRight, Plus } from 'lucide-react';

import { PreviewArt } from '../app/PreviewArt';
import { TodayCard, TodaySkeleton } from './DashboardView';
import { PARTNER_URL, Wordmark } from './PartnerBanner';
import '../../styles/pages/app-home.css';

const app = (t, key, options) => t(`pages.Dashboard.app.${key}`, options);
const home = (t, key, options) => t(`pages.Dashboard.home.${key}`, options);

const Row = ({ to, preview, title, line, onClick }) => (
  <li>
    <Link className="mt-home-row" to={to} onClick={onClick}>
      <span className="mt-home-row__art">{preview}</span>
      <span className="mt-home-row__text">
        <span className="mt-home-row__title">{title}</span>
        <span className="mt-home-row__line">{line}</span>
      </span>
      <ChevronRight className="mt-home-row__end" aria-hidden="true" />
    </Link>
  </li>
);

const RowSkeleton = () => (
  <li>
    <span className="mt-home-row">
      <span className="mt-skel mt-home-row__art" />
      <span className="mt-skel mt-home-row__skel" />
    </span>
  </li>
);

// The partner's card from the site's dashboard, as the list's last row: the Portuguese site only
const PartnerRow = ({ onOpen }) => {
  const { t } = useTranslation();

  if (t('lang') !== 'pt') return null;

  return (
    <li>
      <a
        className="mt-home-row mt-partner"
        href={PARTNER_URL}
        target="_blank"
        rel="noopener sponsored"
        onClick={onOpen}
      >
        <span className="mt-home-row__text">
          <span className="mt-home-row__title">
            <Wordmark />
          </span>
          <span className="mt-home-row__line mt-home-row__line--wrap">
            {t('components.PartnerBanner.partner')} ·{' '}
            {t('components.PartnerBanner.pitch')}
          </span>
        </span>
        <ArrowUpRight className="mt-home-row__end" aria-hidden="true" />
        <span className="mt-sr-only">
          {' '}
          {t('components.PartnerBanner.newTab')}
        </span>
      </a>
    </li>
  );
};

const foodLine = (t, diet) => {
  if (!diet) return app(t, 'foodEmpty');
  const key = { left: 'foodLeft', over: 'foodOver', done: 'foodDone' }[
    diet.status
  ];
  return app(t, key, {
    eaten: diet.eaten,
    target: diet.target,
    kcal: diet.left,
  });
};

// No workout yet: a card with what a workout holds and the way to make the first one
const FirstWorkout = () => {
  const { t } = useTranslation();

  return (
    <section
      className="mt-dash-card mt-home-first"
      aria-labelledby="mt-home-first-title"
    >
      <PreviewArt kind="aiWorkout" size="lg" />
      <h2 id="mt-home-first-title" className="mt-home-first__title">
        {home(t, 'noWorkoutTitle')}
      </h2>
      <p className="mt-home-first__body">{app(t, 'firstBody')}</p>
      <Link
        className="mt-btn mt-btn--primary mt-home-first__create"
        to="/workout"
      >
        <Plus aria-hidden="true" />
        {home(t, 'createWorkout')}
      </Link>
    </section>
  );
};

/**
 * The installed app's home on a phone (owner decision 30/10/2026, option A · Hoje, after OdeParfum's): today's workout
 * one tap from starting, then a short list of the person's things, each row with a small preview of what's behind it.
 * The greeting sits under the floating header's row, like every app page's first line.
 * All data arrives already computed from Dashboard.jsx.
 */
export const AppHomeView = ({
  greetingName,
  dateLine,
  today,
  week,
  volume,
  workouts,
  diet,
  isFree,
  onStart,
  onOpenToday,
  onNavigate,
  onOpenPartner,
  loading = {},
}) => {
  const { t } = useTranslation();
  const after = workouts[1];
  const lock = isFree ? 'lock' : undefined;

  return (
    <div
      className="mt-home"
      aria-busy={loading.today || loading.name ? 'true' : undefined}
    >
      <div className="mt-home__top">
        <h1 className="mt-home__greeting">
          {loading.name ? (
            <span className="mt-skel mt-home__skel" aria-hidden="true" />
          ) : (
            t('pages.Dashboard.greeting', { name: greetingName })
          )}
        </h1>
        <p className="mt-home__date">{dateLine}</p>
      </div>

      {loading.today ? (
        <TodaySkeleton />
      ) : today ? (
        <TodayCard today={today} onStart={onStart} onOpen={onOpenToday} />
      ) : (
        <FirstWorkout />
      )}

      <ul className="mt-dash-card mt-home-list">
        {loading.today ? (
          [0, 1, 2].map((key) => <RowSkeleton key={key} />)
        ) : (
          <>
            {today && (
              <Row
                to="/progress"
                onClick={() => onNavigate('progress')}
                preview={
                  <PreviewArt
                    kind="ring"
                    size="sm"
                    value={{ share: week.progress, label: week.sessions }}
                  />
                }
                title={home(t, 'weekTitle')}
                line={app(t, 'week', {
                  done: week.sessions,
                  count: week.target,
                  volume: volume.value,
                  unit: volume.unit,
                })}
              />
            )}
            {today && (
              <Row
                to="/workouts"
                onClick={() => onNavigate('workouts')}
                preview={
                  <PreviewArt
                    kind="letters"
                    size="sm"
                    value={{
                      letters: workouts
                        .slice(0, 3)
                        .map((workout) => workout.letter),
                    }}
                  />
                }
                title={home(t, 'myWorkouts')}
                line={
                  after
                    ? app(
                        t,
                        after.muscles ? 'workoutsAfter' : 'workoutsAfterBare',
                        { title: after.title, muscles: after.muscles }
                      )
                    : app(t, 'workoutsCount', { count: workouts.length })
                }
              />
            )}
            <Row
              to={diet ? '/calorie-tracker' : '/diet'}
              onClick={() => onNavigate('food')}
              preview={
                diet ? (
                  <PreviewArt
                    kind="ring"
                    size="sm"
                    value={{ share: Math.min(diet.progress, 1) }}
                  />
                ) : (
                  <PreviewArt kind="rings" size="sm" badge={lock} />
                )
              }
              title={home(t, 'food')}
              line={foodLine(t, diet)}
            />
            <Row
              to="/academy"
              onClick={() => onNavigate('academy')}
              preview={<PreviewArt kind="lesson" size="sm" badge={lock} />}
              title={home(t, 'academy')}
              line={app(t, 'academyLine')}
            />
            <Row
              to="/sync"
              onClick={() => onNavigate('alexa')}
              preview={<PreviewArt kind="echo" size="sm" />}
              title={home(t, 'alexa')}
              line={home(t, 'alexaSub')}
            />
            {isFree && (
              <Row
                to="/plans"
                onClick={() => onNavigate('plans')}
                preview={<PreviewArt kind="pro" size="sm" />}
                title={t('pages.Account.discoverPro')}
                line={app(t, 'proLine')}
              />
            )}
            <PartnerRow onOpen={onOpenPartner} />
          </>
        )}
      </ul>
    </div>
  );
};
